/*
 * Submission detail — renders a single row's full content. Used inside
 * the modal opened from "Recent submissions". Layout adapts to the
 * tool, with a JSON block at the bottom so an analyst can verify the
 * raw payload exactly.
 */

import { fmtDateTime, fmtMoney, frictionLabel, flexLabel, relTime } from '../format';
import { ddwPayload, planPayload, sgPayload } from '../aggregate';
import type { AnalyticsRow, DdwVendorPayload } from '../types';

interface SubmissionDetailProps {
  row: AnalyticsRow;
}

export function SubmissionDetail({ row }: SubmissionDetailProps) {
  return (
    <>
      <div className="detail-row">
        <div className="detail-row__label">Submitted</div>
        <div className="detail-row__value">
          {fmtDateTime(row.submitted_at)} · {relTime(row.submitted_at)}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Tool / version</div>
        <div className="detail-row__value">
          {row.tool_id} · schema {row.schema_version}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Submission ID</div>
        <div className="detail-row__value">
          <code>{row.submission_id}</code>
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Anonymous client_id</div>
        <div className="detail-row__value">
          <code>{row.client_id}</code>
        </div>
      </div>

      {row.tool_id === 'plan_builder' && <PlanDetail row={row} />}
      {row.tool_id === 'ddw' && <DdwDetail row={row} />}
      {row.tool_id === 'stay_or_go' && <StayOrGoDetail row={row} />}

      <div className="detail-row">
        <div className="detail-row__label">Raw payload</div>
        <div className="detail-row__value">
          <pre>{JSON.stringify(row.payload, null, 2)}</pre>
        </div>
      </div>
    </>
  );
}

function PlanDetail({ row }: { row: AnalyticsRow }) {
  const p = planPayload(row);
  if (!p) return null;
  const lens = p.lens ?? {};
  return (
    <>
      <div className="detail-row">
        <div className="detail-row__label">Job statement</div>
        <div className="detail-row__value">{lens.jobStatement ?? '—'}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Friction</div>
        <div className="detail-row__value">{frictionLabel(lens.frictionPhase)}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Flexibility</div>
        <div className="detail-row__value">{flexLabel(lens.flexibility)}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Outcomes</div>
        <div className="detail-row__value">
          {(lens.outcomes ?? []).filter(Boolean).map((o, i) => (
            <div key={i}>
              {i + 1}. {o}
            </div>
          )) || '—'}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Diagnostic</div>
        <div className="detail-row__value">
          Stage {p.computed?.stage ?? '—'} ({p.computed?.stage_name ?? ''}) · Joy{' '}
          {p.computed?.joy_score ?? '—'}/100 · Weakest:{' '}
          {(p.computed?.weakest_drivers ?? []).join(', ') || '—'}
        </div>
      </div>
    </>
  );
}

function DdwDetail({ row }: { row: AnalyticsRow }) {
  const p = ddwPayload(row);
  if (!p) return null;
  const lens = p.lens ?? {};
  const vendors = (p.vendors ?? []).filter((v) => v.scored);
  return (
    <>
      <div className="detail-row">
        <div className="detail-row__label">Job statement</div>
        <div className="detail-row__value">{lens.jobStatement ?? '—'}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Friction</div>
        <div className="detail-row__value">{frictionLabel(lens.frictionPhase)}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Flexibility</div>
        <div className="detail-row__value">{flexLabel(lens.flexibility)}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Want to feel</div>
        <div className="detail-row__value">
          {(lens.emotional?.feel ?? []).map((f) => (
            <span key={f} className="inline-pill inline-pill--success" style={{ marginRight: 4 }}>
              {f}
            </span>
          )) || '—'}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Want to stop</div>
        <div className="detail-row__value">
          {(lens.emotional?.avoid ?? []).map((a) => (
            <span key={a} className="inline-pill inline-pill--warn" style={{ marginRight: 4 }}>
              {a}
            </span>
          )) || '—'}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Baseline adaptation tax</div>
        <div className="detail-row__value">{fmtMoney(lens.tax?.calculatedAnnual ?? null)}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Vendors scored · {vendors.length}</div>
        <div className="detail-row__value">
          {vendors.length === 0
            ? '—'
            : vendors.map((v) => <VendorRow key={v.id ?? v.name} vendor={v} />)}
        </div>
      </div>
      {p.research && (
        <div className="detail-row">
          <div className="detail-row__label">Research answers</div>
          <div className="detail-row__value">
            {(['q1', 'q2', 'q3'] as const).map((qk) => {
              const v = p.research?.[qk];
              const text = typeof v === 'string' ? v : v?.answer ?? '';
              const q =
                typeof v === 'object' && v?.question
                  ? v.question
                  : qk === 'q1'
                    ? 'Trigger'
                    : qk === 'q2'
                      ? 'Decision team'
                      : 'Timeline';
              if (!text) return null;
              return (
                <div key={qk} style={{ marginBottom: 6 }}>
                  <strong style={{ fontSize: 11, color: 'var(--textMuted)' }}>{q}</strong>
                  <div style={{ fontSize: 12, marginTop: 2 }}>{text}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function VendorRow({ vendor }: { vendor: DdwVendorPayload }) {
  const adoption = vendor.adoption_confidence ?? vendor.adoptionConfidence;
  return (
    <div className="vendor-card">
      <span className="vendor-card__title">{vendor.name ?? 'Unnamed vendor'}</span>
      {vendor.category && (
        <span className="inline-pill" style={{ marginLeft: 6 }}>
          {vendor.category}
        </span>
      )}
      <div className="vendor-card__meta">
        gut {vendor.score?.gut ?? '—'}/10 · emotional {vendor.score?.emotionalFit ?? '—'}/5 ·
        adoption {adoption ?? '—'}% · {vendor.score?.ownership ?? '—'} ownership
      </div>
    </div>
  );
}

function StayOrGoDetail({ row }: { row: AnalyticsRow }) {
  const p = sgPayload(row);
  if (!p) return null;
  const c = p.computed ?? {};
  return (
    <>
      <div className="detail-row">
        <div className="detail-row__label">Team / tier</div>
        <div className="detail-row__value">
          {p.team ?? '—'} · {p.tier ?? '—'}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Stay Score</div>
        <div className="detail-row__value">
          {c.score ?? '—'}/10 · {c.verdictLabel ?? '—'}
        </div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Cost of staying (24mo)</div>
        <div className="detail-row__value">{fmtMoney(c.costOfStaying24mo ?? null)}</div>
      </div>
      <div className="detail-row">
        <div className="detail-row__label">Cost of switching</div>
        <div className="detail-row__value">{fmtMoney(c.costOfSwitching ?? null)}</div>
      </div>
    </>
  );
}
